import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DoorOpen, 
  Wrench, 
  Users, 
  Maximize2, 
  Minimize2,
  Settings,
  RefreshCw,
  Clock
} from 'lucide-react';

type RoomState = 'available' | 'occupied' | 'maintenance';
type WidgetSize = 'small' | 'medium' | 'large';
type WidgetMode = 'full' | 'compact' | 'emoji';

interface Room {
  id: string;
  name: string;
  state: RoomState;
  capacity: number;
  lastUpdated: string;
}

const rooms: Room[] = [
  { id: '1', name: 'House', state: 'available', capacity: 12, lastUpdated: '2 min ago' },
  { id: '2', name: 'Call Availability', state: 'occupied', capacity: 4, lastUpdated: 'Just now' },
  { id: '3', name: 'Birthday Mode', state: 'maintenance', capacity: 8, lastUpdated: '5 min ago' },
  { id: '4', name: 'Study Time', state: 'available', capacity: 20, lastUpdated: '1 min ago' },
];

const stateColors = {
  available: 'bg-green-500',
  occupied: 'bg-red-500',
  maintenance: 'bg-yellow-500',
};

const stateIcons = {
  available: <DoorOpen className="w-6 h-6" />,
  occupied: <Users className="w-6 h-6" />,
  maintenance: <Wrench className="w-6 h-6" />,
};

const stateEmojis = {
  available: '🟢',
  occupied: '🔴',
  maintenance: '🟡',
};

const DashboardAnimation: React.FC = () => {
  const [widgetSizes, setWidgetSizes] = useState<Record<string, WidgetSize>>({
    '1': 'large',
    '2': 'medium',
    '3': 'small',
    '4': 'medium',
  });

  const [widgetModes, setWidgetModes] = useState<Record<string, WidgetMode>>({
    '1': 'full',
    '2': 'compact',
    '3': 'emoji',
    '4': 'full',
  });

  const [roomsState, setRoomsState] = useState<Room[]>(rooms);
  const [isAnimating, setIsAnimating] = useState(false);

  // Auto-update demonstration
  useEffect(() => {
    const interval = setInterval(() => {
      const randomRoomIndex = Math.floor(Math.random() * rooms.length);
      const randomState = ['available', 'occupied', 'maintenance'][Math.floor(Math.random() * 3)] as RoomState;
      
      setRoomsState(prev => prev.map((room, index) => 
        index === randomRoomIndex 
          ? { ...room, state: randomState, lastUpdated: 'Just now' }
          : room
      ));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const toggleSize = (roomId: string) => {
    setWidgetSizes(prev => ({
      ...prev,
      [roomId]: prev[roomId] === 'small' ? 'medium' : prev[roomId] === 'medium' ? 'large' : 'small'
    }));
  };

  const toggleMode = (roomId: string) => {
    setWidgetModes(prev => ({
      ...prev,
      [roomId]: prev[roomId] === 'full' ? 'compact' : prev[roomId] === 'compact' ? 'emoji' : 'full'
    }));
  };

  const updateRoomState = (roomId: string, newState: RoomState) => {
    setIsAnimating(true);
    setRoomsState(prev => prev.map(room => 
      room.id === roomId 
        ? { ...room, state: newState, lastUpdated: 'Just now' }
        : room
    ));
    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <AnimatePresence>
          {roomsState.map((room) => (
            <motion.div
              key={room.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden
                ${widgetSizes[room.id] === 'large' ? 'col-span-2 row-span-2' : 
                  widgetSizes[room.id] === 'medium' ? 'col-span-2' : 'col-span-1'}`}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-3">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                    {room.name}
                  </h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => toggleSize(room.id)}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      {widgetSizes[room.id] === 'large' ? 
                        <Minimize2 className="w-3 h-3" /> : 
                        <Maximize2 className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => toggleMode(room.id)}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Settings className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                <motion.div 
                  className={`${stateColors[room.state]} p-3 rounded-lg mb-3 flex items-center justify-center`}
                  animate={{ scale: isAnimating ? [1, 1.05, 1] : 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {widgetModes[room.id] === 'emoji' ? (
                    <span className="text-3xl">{stateEmojis[room.state]}</span>
                  ) : widgetModes[room.id] === 'compact' ? (
                    <div className="flex items-center gap-2">
                      {stateIcons[room.state]}
                      <span className="font-medium text-white capitalize text-sm">{room.state}</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        {stateIcons[room.state]}
                        <span className="font-medium text-white capitalize text-sm">{room.state}</span>
                      </div>
                      <div className="text-white/80 text-xs flex items-center justify-center gap-1">
                        <Users className="w-3 h-3" />
                        {room.capacity} people
                      </div>
                      <div className="text-white/80 text-xs flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3" />
                        {room.lastUpdated}
                      </div>
                    </div>
                  )}
                </motion.div>

                {widgetSizes[room.id] !== 'small' && (
                  <div className="flex gap-1 justify-center">
                    <button
                      onClick={() => updateRoomState(room.id, 'available')}
                      className="px-2 py-1 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors text-xs"
                    >
                      Available
                    </button>
                    <button
                      onClick={() => updateRoomState(room.id, 'occupied')}
                      className="px-2 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-xs"
                    >
                      Occupied
                    </button>
                    <button
                      onClick={() => updateRoomState(room.id, 'maintenance')}
                      className="px-2 py-1 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors text-xs"
                    >
                      Maintenance
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DashboardAnimation; 